/**
 * OnboardingRitual
 * A 10-minute initiation experience. Not a form — a ceremony.
 * Steps: Welcome → Name → Intention → Baseline (3 PHQ items) → Voice → First moment → ERI reveal
 * Replaces the legacy Onboarding.jsx at /onboarding.
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AudioRecorder from '@/components/AudioRecorder';
import orbUrl from '../orbAsset';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Focus areas ─────────────────────────────────────────────────────────────
const FOCUS_AREAS = [
  { id: 'anxiety',    label: 'Anxiety & Overwhelm',  sub: 'Calm the alarm' },
  { id: 'grief',      label: 'Grief & Loss',          sub: 'Return to yourself' },
  { id: 'burnout',    label: 'Burnout',               sub: 'Restore energy' },
  { id: 'trauma',     label: 'Trauma',                sub: 'Integrate gently' },
  { id: 'growth',     label: 'Personal Growth',       sub: 'Build who you are' },
  { id: 'regulation', label: 'Daily Regulation',      sub: 'Stay grounded' },
];

// ── Quick 3-question PHQ screen ───────────────────────────────────────────────
const QUICK_PHQ = [
  { q: 'Over the last 2 weeks, how often have you felt little interest or pleasure in doing things?', key: 'phq1' },
  { q: 'How often have you felt down, depressed, or hopeless?', key: 'phq2' },
  { q: 'How often have you felt nervous, anxious, or on edge?', key: 'gad1' },
];
const FREQ_OPTS = [
  { label: 'Not at all', value: 0 },
  { label: 'Several days', value: 1 },
  { label: 'More than half', value: 2 },
  { label: 'Nearly every day', value: 3 },
];

// ── Calibration script for voice recording ───────────────────────────────────
const VOICE_SCRIPT = `My voice carries something older than words. It holds the memory of my body, the texture of my emotions, and the thread that connects me to this present moment.

Throughout life, we learn to regulate through others — a parent's voice, a teacher's tone, someone we trusted. HYPSEYA invites something different: that you become your own source of regulation.

When the nervous system is dysregulated, it searches for signals of safety. The voice is one of those signals — not for what it says, but for how it sounds. Its frequency, its rhythm, the breath behind the words.

Right now, you are present. You are here. And that is enough.`;

// ── Step definitions ─────────────────────────────────────────────────────────
// welcome | name | intention | baseline | voice | moment | reveal | done

// ── Shared styles ─────────────────────────────────────────────────────────────
const DARK_BG = {
  minHeight: '100vh',
  background: 'linear-gradient(160deg, #071A19 0%, #0D2B2A 50%, #1A4A47 100%)',
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  padding: '0 28px',
  textAlign: 'center',
};

const LIGHT_BG = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #F7F3EC 0%, #EEF4F3 100%)',
  display: 'flex', flexDirection: 'column',
  padding: '60px 24px 100px',
};

function RippleOrb({ size = 110, delay = 3.2 }) {
  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      {[0, delay / 4, delay / 2, (3 * delay) / 4].map((d, i) => (
        <div key={i} style={{
          position: 'absolute', top: '50%', left: '50%',
          width: size - 10, height: size - 10, borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.45)',
          animation: `rplOut ${delay}s ease-out ${d}s infinite`,
        }} />
      ))}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: '12px', height: '12px', transform: 'translate(-50%,-50%)',
        borderRadius: '50%', background: 'rgba(255,255,255,0.9)',
        boxShadow: '0 0 18px rgba(255,255,255,0.45)',
      }} />
      <style>{`@keyframes rplOut{0%{transform:translate(-50%,-50%) scale(.05);opacity:.9}100%{transform:translate(-50%,-50%) scale(1);opacity:0}}`}</style>
    </div>
  );
}

function CTAButton({ onClick, children, style = {} }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '16px 44px',
        background: 'rgba(255,255,255,0.12)',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: '50px', color: '#fff',
        fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer',
        letterSpacing: '0.04em',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function ProgressDots({ current, total }) {
  return (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '40px' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === current ? '20px' : '6px', height: '6px', borderRadius: '3px',
          background: i <= current ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.15)',
          transition: 'all 0.4s ease',
        }} />
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function OnboardingRitual() {
  const navigate = useNavigate();
  const [step, setStep]         = useState('welcome');
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [focusArea, setFocusArea] = useState(null);
  const [quickScores, setQuickScores] = useState({});
  const [voiceBlob, setVoiceBlob]   = useState(null);
  const [voiceName, setVoiceName]   = useState('');
  const [cloningVoice, setCloningVoice] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [user, setUser]         = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [eriScore, setEriScore] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [visible, setVisible]   = useState(true);
  const nameRef = useRef(null);
  const emailRef = useRef(null);

  // Skip if already onboarded
  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) {
      try {
        const parsed = JSON.parse(u);
        if (parsed?.has_completed_onboarding) navigate('/');
      } catch {}
    }
  }, [navigate]);

  const transition = (nextStep) => {
    setVisible(false);
    setTimeout(() => { setStep(nextStep); setVisible(true); }, 280);
  };

  // ── Create user on backend ─────────────────────────────────────────────────
  const createUser = async () => {
    setSaving(true);
    try {
      const userData = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        country: 'Unknown',
        language: navigator.language?.startsWith('pt') ? 'pt' : 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        focus_area: focusArea,
        intentions: [focusArea],
        intention: focusArea,
        expanded_states: 'no',
        has_completed_onboarding: false,
        gdpr_consent_given: consentGiven,
        gdpr_consent_date: consentGiven ? new Date().toISOString() : null,
        gdpr_consent_version: '1.0',
      };
      const res = await axios.post(`${API}/users`, userData);
      const u = res.data;
      localStorage.setItem('user', JSON.stringify(u));
      localStorage.setItem('userEmail', u.email);
      localStorage.setItem('userName', u.name);
      setUser(u);
      return u;
    } catch (e) {
      // User may already exist
      if (e.response?.status === 400) {
        try {
          const res = await axios.get(`${API}/users/${email.trim().toLowerCase()}`);
          const u = res.data;
          localStorage.setItem('user', JSON.stringify(u));
          setUser(u);
          return u;
        } catch {}
      }
      toast.error('Something went wrong. Please try again.');
      return null;
    } finally { setSaving(false); }
  };

  // ── Clone voice ───────────────────────────────────────────────────────────
  const cloneVoice = async (u) => {
    if (!voiceBlob || !u?.user_id) return;
    setCloningVoice(true);
    try {
      const fd = new FormData();
      fd.append('user_id', u.user_id);
      fd.append('voice_name', voiceName || `${name}'s Voice`);
      fd.append('audio_file', voiceBlob, 'recording.webm');
      await axios.post(`${API}/voice-profiles/clone`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setVoiceReady(true);
      // Update local user cache
      const updated = { ...u, cloned_voice_status: 'ready' };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
    } catch {
      // Non-fatal — continue without voice
    } finally { setCloningVoice(false); }
  };

  // ── Save baseline assessment ──────────────────────────────────────────────
  const saveBaseline = async (u) => {
    if (!u?.user_id || Object.keys(quickScores).length < 3) return;
    try {
      await axios.post(`${API}/assessments`, {
        user_id: u.user_id,
        phq9_answers: [quickScores.phq1 ?? 0, quickScores.phq2 ?? 0, 0, 0, 0, 0, 0, 0, 0],
        gad7_answers: [quickScores.gad1 ?? 0, 0, 0, 0, 0, 0, 0],
        type: 'baseline_quick',
      });
    } catch {}
  };

  // ── Complete onboarding ───────────────────────────────────────────────────
  const completeOnboarding = async (u) => {
    if (!u?.user_id) return;
    try {
      await axios.put(`${API}/users/${u.email}`, {
        has_completed_onboarding: true,
        intention: focusArea,
        // Persist GDPR consent here too: the user already exists (synced on
        // login), so the POST /users above returns 400 and never stored it.
        gdpr_consent_given: consentGiven,
        gdpr_consent_date: consentGiven ? new Date().toISOString() : null,
        gdpr_consent_version: '1.0',
      });
      const updated = { ...u, has_completed_onboarding: true };
      localStorage.setItem('user', JSON.stringify(updated));
    } catch {}
  };

  // ── STEP FLOWS ─────────────────────────────────────────────────────────────

  const handleNameNext = () => {
    if (!name.trim()) { toast.error('What shall we call you?'); return; }
    if (!email.trim() || !email.includes('@')) { toast.error('A valid email is needed.'); return; }
    transition('consent');
  };

  const handleConsentNext = () => {
    if (!consentGiven) { toast.error('Please read and accept the consent to continue.'); return; }
    transition('intention');
  };

  const handleIntentionNext = async () => {
    if (!focusArea) { toast.error('Choose what brings you here.'); return; }
    transition('baseline');
  };

  const handleBaselineNext = () => {
    if (Object.keys(quickScores).length < 3) {
      toast.error('Please answer all three questions.');
      return;
    }
    transition('voice');
  };

  const handleVoiceNext = async () => {
    setSaving(true);
    const u = await createUser();
    if (!u) return;
    await saveBaseline(u);
    if (voiceBlob) await cloneVoice(u);
    setSaving(false);
    transition('moment');
  };

  const handleMomentNext = async () => {
    transition('reveal');
    // Simulate ERI calculation (real one happens after first session)
    const baseline = (quickScores.phq1 ?? 0) + (quickScores.phq2 ?? 0) + (quickScores.gad1 ?? 0);
    const estimatedERI = Math.max(30, Math.round(70 - baseline * 5));
    setEriScore(estimatedERI);
  };

  const handleRevealNext = async () => {
    if (user) await completeOnboarding(user);
    navigate('/');
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────

  const fadeStyle = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(12px)',
    transition: 'opacity 0.28s ease, transform 0.28s ease',
  };

  const STEPS_DARK  = ['welcome', 'moment', 'reveal'];
  const isDark = STEPS_DARK.includes(step);
  const containerStyle = isDark ? DARK_BG : { ...LIGHT_BG };

  const TOTAL_DOTS = 6;
  const DOT_INDEX  = { welcome: 0, name: 1, intention: 2, baseline: 3, voice: 4, moment: 5, reveal: 5 };

  return (
    <div style={containerStyle}>
      <div style={{ ...fadeStyle, width: '100%', maxWidth: '480px' }}>

        {/* ── WELCOME ── */}
        {step === 'welcome' && (
          <div style={{ textAlign: 'center', padding: '0 8px' }}>
            <RippleOrb size={130} delay={3.2} />
            <div style={{ marginTop: '36px', marginBottom: '10px' }}>
              <p style={{ margin: '0 0 6px', fontSize: '0.65rem', fontWeight: '700', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                Welcome to
              </p>
              <img src={orbUrl} alt="HYPSEYA" style={{ width: '52px', marginBottom: '10px' }} />
              <h1 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '2rem', fontWeight: '400', lineHeight: 1.2 }}>
                HYPSEYA
              </h1>
            </div>
            <p style={{ margin: '20px auto 40px', color: 'rgba(255,255,255,0.55)', fontSize: '1rem', lineHeight: 1.75, maxWidth: '320px' }}>
              Emotional technology.<br />
              Built around your own voice.
            </p>
            <CTAButton onClick={() => transition('privacy')}>Begin →</CTAButton>
          </div>
        )}

        {/* ── PRIVACY NOTICE ── */}
        {step === 'privacy' && (
          <div style={{ textAlign: 'center', padding: '0 8px' }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '50%',
              background: 'rgba(30,107,104,0.15)', border: '1.5px solid rgba(184,211,207,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 28px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(184,211,207,0.85)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h2 style={{ margin: '0 0 10px', fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', color: '#fff', fontWeight: '400' }}>
              Your data is yours.
            </h2>
            <p style={{ margin: '0 auto 28px', color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', lineHeight: 1.75, maxWidth: '300px' }}>
              Before you begin, here's how we treat your information:
            </p>
            <div style={{
              background: 'rgba(255,255,255,0.05)', borderRadius: '16px',
              padding: '20px', marginBottom: '28px', textAlign: 'left',
              border: '1px solid rgba(184,211,207,0.15)',
            }}>
              {[
                ['🔒', 'Encrypted & stored securely — never on your device without your control'],
                ['🚫', 'Never sold or shared with third parties'],
                ['🧠', 'Mental health data is pseudonymised before any AI processing'],
                ['🇪🇺', 'EU GDPR compliant — you can export or delete your data at any time'],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: '1px' }}>{icon}</span>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{text}</p>
                </div>
              ))}
            </div>
            <p style={{ margin: '0 auto 28px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1.6, maxWidth: '280px' }}>
              By continuing, you agree to our{' '}
              <a href="/privacy" target="_blank" style={{ color: 'rgba(184,211,207,0.7)', textDecoration: 'underline' }}>Privacy Policy</a>
              {' '}and{' '}
              <a href="/terms" target="_blank" style={{ color: 'rgba(184,211,207,0.7)', textDecoration: 'underline' }}>Terms of Service</a>.
            </p>
            <CTAButton onClick={() => transition('name')}>I understand — continue →</CTAButton>
          </div>
        )}

        {/* ── NAME ── */}
        {step === 'name' && (
          <div style={{ paddingTop: '20px' }}>
            <ProgressDots current={DOT_INDEX.name} total={TOTAL_DOTS} />
            <p style={{ margin: '0 0 6px', fontSize: '0.68rem', fontWeight: '700', color: '#9AADAB', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Step 1 of 6
            </p>
            <h2 style={{ margin: '0 0 8px', fontFamily: "'Playfair Display', serif", fontSize: '1.7rem', color: '#1E3A3A', fontWeight: '400' }}>
              Who are you?
            </h2>
            <p style={{ margin: '0 0 32px', color: '#9AADAB', fontSize: '0.88rem', lineHeight: 1.6 }}>
              Your name is how HYPSEYA will know you.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '32px' }}>
              <input
                ref={nameRef}
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && emailRef.current?.focus()}
                placeholder="Your name"
                autoFocus
                style={{
                  width: '100%', padding: '16px 18px', fontSize: '1rem',
                  borderRadius: '14px', border: '1.5px solid rgba(30,107,104,0.2)',
                  background: 'rgba(255,255,255,0.9)', color: '#1E3A3A',
                  outline: 'none', boxSizing: 'border-box',
                  fontFamily: "'Playfair Display', serif",
                }}
              />
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleNameNext()}
                placeholder="Your email"
                style={{
                  width: '100%', padding: '16px 18px', fontSize: '0.95rem',
                  borderRadius: '14px', border: '1.5px solid rgba(30,107,104,0.2)',
                  background: 'rgba(255,255,255,0.9)', color: '#1E3A3A',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <button
              onClick={handleNameNext}
              style={{
                width: '100%', padding: '16px',
                background: name.trim() && email.includes('@')
                  ? 'linear-gradient(135deg, #1E6B68, #2D8B87)'
                  : 'rgba(184,211,207,0.4)',
                color: '#fff', border: 'none', borderRadius: '50px',
                fontSize: '0.95rem', fontWeight: '600',
                cursor: name.trim() ? 'pointer' : 'default',
              }}
            >
              Continue →
            </button>
          </div>
        )}

        {/* ── CONSENT (Art. 9 GDPR) ── */}
        {step === 'consent' && (
          <div style={{ paddingTop: '20px' }}>
            <ProgressDots current={1} total={TOTAL_DOTS} />

            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>🔒</div>
              <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: '700', color: '#9AADAB', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Your data, your control
              </p>
            </div>

            <h2 style={{ margin: '12px 0 8px', fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', color: '#1E3A3A', fontWeight: '400', lineHeight: 1.2 }}>
              Before we go deeper,<br />
              <em style={{ color: '#1E6B68' }}>a few words.</em>
            </h2>

            <div style={{ background: 'rgba(255,255,255,0.75)', borderRadius: '16px', padding: '20px', margin: '20px 0', border: '1px solid rgba(30,107,104,0.12)', textAlign: 'left' }}>
              <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: '#2C3E3D', lineHeight: 1.7 }}>
                HYPSEYA collects sensitive health data — including emotional states, mood scores, and biometric signals. Under European law (GDPR Article 9), this requires your <strong>explicit and informed consent</strong>.
              </p>
              <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: '#2C3E3D', lineHeight: 1.7 }}>
                <strong>What we collect:</strong> ERI scores, PHQ-9/GAD-7 responses, mood & energy check-ins, journal entries, voice recordings, biometric signals (HRV, sleep).
              </p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#2C3E3D', lineHeight: 1.7 }}>
                <strong>What we never do:</strong> sell your data, share journal entries with third parties, or use your data to train external AI models.
              </p>
            </div>

            {/* Art. 9 checkbox */}
            <label style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '28px', textAlign: 'left' }}>
              <div
                onClick={() => setConsentGiven(v => !v)}
                style={{
                  width: 24, height: 24, borderRadius: '6px', flexShrink: 0, marginTop: '2px',
                  border: `2px solid ${consentGiven ? '#1E6B68' : 'rgba(30,107,104,0.35)'}`,
                  background: consentGiven ? '#1E6B68' : 'rgba(255,255,255,0.8)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                }}
              >
                {consentGiven && <span style={{ color: 'white', fontSize: '14px', lineHeight: 1 }}>✓</span>}
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#2C3E3D', lineHeight: 1.65 }}>
                I expressly consent to HYPSEYA processing my mental and emotional health data (ERI, mood, journal entries, voice, biometrics) for personalised emotional training, in accordance with <strong>Article 9(2)(a) of the GDPR</strong>. I understand I can withdraw this consent and request deletion of my data at any time.
              </p>
            </label>

            <p style={{ fontSize: '0.72rem', color: '#9AADAB', marginBottom: '20px', textAlign: 'left', lineHeight: 1.5 }}>
              By continuing you also accept our{' '}
              <a href="/privacy" target="_blank" style={{ color: '#1E6B68', textDecoration: 'underline' }}>Privacy Policy</a>.
              Consent recorded with timestamp and version 1.0.
            </p>

            <button
              onClick={handleConsentNext}
              style={{
                width: '100%', padding: '16px',
                background: consentGiven ? 'linear-gradient(135deg, #1E6B68, #2D8B87)' : 'rgba(30,107,104,0.25)',
                border: 'none', borderRadius: '14px', color: 'white',
                fontSize: '1rem', fontWeight: '600', cursor: consentGiven ? 'pointer' : 'default',
                transition: 'all 0.3s',
              }}
            >
              I understand and consent →
            </button>
          </div>
        )}

        {/* ── INTENTION ── */}
        {step === 'intention' && (
          <div style={{ paddingTop: '20px' }}>
            <ProgressDots current={DOT_INDEX.intention} total={TOTAL_DOTS} />
            <p style={{ margin: '0 0 6px', fontSize: '0.68rem', fontWeight: '700', color: '#9AADAB', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Step 2 of 6
            </p>
            <h2 style={{ margin: '0 0 8px', fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', color: '#1E3A3A', fontWeight: '400' }}>
              What brings you here{name ? `, ${name.split(' ')[0]}` : ''}?
            </h2>
            <p style={{ margin: '0 0 28px', color: '#9AADAB', fontSize: '0.85rem', lineHeight: 1.6 }}>
              This shapes everything — your practices, your metrics, your meditations.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              {FOCUS_AREAS.map(f => (
                <button
                  key={f.id}
                  onClick={() => setFocusArea(f.id)}
                  style={{
                    width: '100%', padding: '16px 20px',
                    background: focusArea === f.id
                      ? 'linear-gradient(135deg, rgba(30,107,104,0.1), rgba(45,139,135,0.06))'
                      : 'rgba(255,255,255,0.9)',
                    border: `1.5px solid ${focusArea === f.id ? '#1E6B68' : 'rgba(184,211,207,0.4)'}`,
                    borderRadius: '14px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '0.9rem', color: focusArea === f.id ? '#1E6B68' : '#1E3A3A' }}>{f.label}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#9AADAB' }}>{f.sub}</p>
                  </div>
                  {focusArea === f.id && (
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#1E6B68', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#fff', fontSize: '0.7rem' }}>✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={handleIntentionNext}
              style={{
                width: '100%', padding: '16px',
                background: focusArea
                  ? 'linear-gradient(135deg, #1E6B68, #2D8B87)'
                  : 'rgba(184,211,207,0.4)',
                color: '#fff', border: 'none', borderRadius: '50px',
                fontSize: '0.95rem', fontWeight: '600',
                cursor: focusArea ? 'pointer' : 'default',
              }}
            >
              Continue →
            </button>
          </div>
        )}

        {/* ── BASELINE ── */}
        {step === 'baseline' && (
          <div style={{ paddingTop: '20px' }}>
            <ProgressDots current={DOT_INDEX.baseline} total={TOTAL_DOTS} />
            <p style={{ margin: '0 0 6px', fontSize: '0.68rem', fontWeight: '700', color: '#9AADAB', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Step 3 of 6
            </p>
            <h2 style={{ margin: '0 0 8px', fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', color: '#1E3A3A', fontWeight: '400' }}>
              3 quick questions
            </h2>
            <p style={{ margin: '0 0 28px', color: '#9AADAB', fontSize: '0.85rem', lineHeight: 1.6 }}>
              This is your baseline. It makes the change visible later.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '28px' }}>
              {QUICK_PHQ.map((item, qi) => (
                <div key={item.key}>
                  <p style={{ margin: '0 0 12px', fontSize: '0.88rem', color: '#1E3A3A', lineHeight: 1.6, fontWeight: '500' }}>
                    {qi + 1}. {item.q}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {FREQ_OPTS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setQuickScores(s => ({ ...s, [item.key]: opt.value }))}
                        style={{
                          padding: '12px 10px',
                          background: quickScores[item.key] === opt.value
                            ? 'linear-gradient(135deg, rgba(30,107,104,0.12), rgba(45,139,135,0.08))'
                            : 'rgba(255,255,255,0.9)',
                          border: `1.5px solid ${quickScores[item.key] === opt.value ? '#1E6B68' : 'rgba(184,211,207,0.4)'}`,
                          borderRadius: '12px', cursor: 'pointer',
                          fontSize: '0.78rem', fontWeight: '500',
                          color: quickScores[item.key] === opt.value ? '#1E6B68' : '#555',
                          transition: 'all 0.15s',
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleBaselineNext}
              style={{
                width: '100%', padding: '16px',
                background: Object.keys(quickScores).length >= 3
                  ? 'linear-gradient(135deg, #1E6B68, #2D8B87)'
                  : 'rgba(184,211,207,0.4)',
                color: '#fff', border: 'none', borderRadius: '50px',
                fontSize: '0.95rem', fontWeight: '600',
                cursor: Object.keys(quickScores).length >= 3 ? 'pointer' : 'default',
              }}
            >
              Continue →
            </button>
          </div>
        )}

        {/* ── VOICE ── */}
        {step === 'voice' && (
          <div style={{ paddingTop: '20px' }}>
            <ProgressDots current={DOT_INDEX.voice} total={TOTAL_DOTS} />
            <p style={{ margin: '0 0 6px', fontSize: '0.68rem', fontWeight: '700', color: '#9AADAB', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Step 4 of 6 · The most important part
            </p>
            <h2 style={{ margin: '0 0 8px', fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', color: '#1E3A3A', fontWeight: '400' }}>
              Record your voice
            </h2>
            <p style={{ margin: '0 0 8px', color: '#9AADAB', fontSize: '0.85rem', lineHeight: 1.6 }}>
              2 minutes. Your voice becomes the anchor in every practice you create.
            </p>
            <p style={{ margin: '0 0 24px', color: '#AAB8B6', fontSize: '0.78rem' }}>
              You can skip this and add it later in the Studio.
            </p>

            {/* Script */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(30,107,104,0.05), rgba(184,211,207,0.1))',
              borderRadius: '16px', padding: '18px 20px',
              border: '1px solid rgba(30,107,104,0.12)', marginBottom: '20px',
            }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.68rem', fontWeight: '700', color: '#1E6B68', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Read this aloud as you record
              </p>
              <p style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '0.88rem', color: '#1E3A3A', lineHeight: 1.9 }}>
                {VOICE_SCRIPT}
              </p>
              <p style={{ margin: '10px 0 0', fontSize: '0.68rem', color: '#B0C4C2', textAlign: 'right' }}>≈ 2 min at a calm pace</p>
            </div>

            {/* Recorder */}
            <AudioRecorder onRecordingComplete={(blob) => {
              setVoiceBlob(blob);
              setVoiceName(`${name.split(' ')[0]}'s Voice`);
            }} />

            {voiceBlob && (
              <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#2D8B87', fontWeight: '600', textAlign: 'center' }}>
                ✓ Recording ready ({Math.round(voiceBlob.size / 1024)} KB)
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '24px' }}>
              <button
                onClick={handleVoiceNext}
                disabled={saving || cloningVoice}
                style={{
                  width: '100%', padding: '16px',
                  background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
                  color: '#fff', border: 'none', borderRadius: '50px',
                  fontSize: '0.95rem', fontWeight: '600',
                  cursor: saving || cloningVoice ? 'not-allowed' : 'pointer',
                  opacity: saving || cloningVoice ? 0.7 : 1,
                }}
              >
                {cloningVoice ? 'Cloning your voice…' : saving ? 'Saving…' : voiceBlob ? 'Clone & Continue →' : 'Continue →'}
              </button>
              {!voiceBlob && (
                <button
                  onClick={handleVoiceNext}
                  disabled={saving}
                  style={{
                    background: 'none', border: 'none',
                    color: '#B0C4C2', fontSize: '0.8rem', cursor: 'pointer',
                    padding: '8px',
                  }}
                >
                  Skip for now
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── MOMENT ── */}
        {step === 'moment' && (
          <div style={{ textAlign: 'center', padding: '0 8px' }}>
            <RippleOrb size={120} delay={3} />
            <div style={{ marginTop: '32px', marginBottom: '40px' }}>
              <p style={{ margin: '0 0 6px', fontSize: '0.65rem', fontWeight: '700', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Step 5 of 6
              </p>
              <h2 style={{ margin: '0 0 16px', fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.65rem', fontWeight: '400', lineHeight: 1.3 }}>
                {voiceReady
                  ? `${name.split(' ')[0]}, your voice is alive.`
                  : 'Your account is ready.'}
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', lineHeight: 1.75, maxWidth: '320px', margin: '0 auto' }}>
                {voiceReady
                  ? 'It is now part of every practice you create. From here, you guide yourself.'
                  : 'You can add your voice anytime in the Studio. Everything else is in place.'}
              </p>
            </div>
            <CTAButton onClick={handleMomentNext}>
              {voiceReady ? 'See your starting point →' : 'Continue →'}
            </CTAButton>
          </div>
        )}

        {/* ── REVEAL ── */}
        {step === 'reveal' && (
          <div style={{ textAlign: 'center', padding: '0 8px' }}>
            <div style={{ margin: '0 auto 28px', width: '90px', height: '90px', position: 'relative' }}>
              <svg viewBox="0 0 100 100" style={{ width: '90px', height: '90px', transform: 'rotate(-90deg)' }}>
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6"/>
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="6"
                  strokeDasharray={`${2 * Math.PI * 42}`}
                  strokeDashoffset={`${2 * Math.PI * 42 * (1 - (eriScore ?? 0) / 100)}`}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 1.5s ease' }}
                />
              </svg>
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)', textAlign: 'center',
              }}>
                <p style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', fontWeight: '700', color: '#fff', lineHeight: 1 }}>
                  {eriScore ?? '—'}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '0.55rem', color: 'rgba(255,255,255,0.45)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' }}>ERI</p>
              </div>
            </div>

            <p style={{ margin: '0 0 6px', fontSize: '0.65rem', fontWeight: '700', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Your starting point
            </p>
            <h2 style={{ margin: '0 0 16px', fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.55rem', fontWeight: '400' }}>
              This is where you begin.
            </h2>
            <p style={{ margin: '0 0 36px', color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', lineHeight: 1.75, maxWidth: '300px', marginLeft: 'auto', marginRight: 'auto' }}>
              Your ERI starts at <strong style={{ color: '#fff' }}>{eriScore}</strong>. Every practice moves it. In 8 weeks, you will see exactly how far you've come.
            </p>
            <CTAButton onClick={handleRevealNext}>
              Enter HYPSEYA →
            </CTAButton>
          </div>
        )}

      </div>
    </div>
  );
}
